import {
	PortableText,
	PortableTextBlock,
	PortableTextComponentProps,
	PortableTextMarkComponentProps,
	PortableTextTypeComponentProps,
} from 'next-sanity';

import { Image } from '@unpic/react/nextjs';
import { PortableText as PortableTextType } from '@/typegen/sanity.types';
import { PortableTextTypeImageComponent } from '@/components/marketing/OverviewDescription/OverviewDescription';
import React from 'react';
import { addQueryParamToUrl } from '../ResponsiveImage';
import { stegaCleanValue } from '@/utils/sanity/stegaCleanValue';
import styles from './RichTextComponent.module.css';

export type TextAlignmentMark = {
	_type: 'textAlignment';
	alignment: 'left' | 'center' | 'right' | 'justify';
};

export type TextColorMark = {
	_type: 'textColor';
	color: 'primary' | 'secondary' | 'accent' | 'Highlight';
	_key?: string;
};

export type StructuredTableBlock = {
	_type: 'table';
	rows: {
		cells: string[];
	}[];
	_key?: string;
};

export default function RichTextComponent({ data }: { data?: PortableTextType | null }) {
	if (!data?.content) {
		return <></>;
	}

	return <PortableText value={data.content} components={CustomPortableTextComponents} />;
}

export const getIndentClass = (level: number = 0) => {
	const map: Record<number, string> = {
		0: '!pl-0',
		1: '!pl-4',
		2: '!pl-8',
		3: '!pl-12',
		4: '!pl-16',
		5: '!pl-20',
		6: '!pl-24',
		7: '!pl-28',
		8: '!pl-32',
		9: '!pl-36',
		10: '!pl-40',
	};
	return map[level];
};

export const superScriptify = (node: React.ReactNode, customClass?: string): React.ReactNode => {
	if (typeof node === 'string') {
		return node.split(/(®)/g).map((part, index) =>
			part === '®' ? (
				<sup key={index} className={customClass ? customClass : `text-xs`}>
					®
				</sup>
			) : (
				part
			)
		);
	}
	if (Array.isArray(node)) {
		return node.map((child, i) => <React.Fragment key={i}>{superScriptify(child)}</React.Fragment>);
	}
	if (React.isValidElement(node)) {
		const element = node as React.ReactElement<{ children?: React.ReactNode }>;
		const children = element?.props?.children;
		if (React.Children.count(children) > 0) {
			const newChildren = superScriptify(children, customClass);
			return React.cloneElement(element, { ...element?.props, children: newChildren });
		}
	}
	return node;
};

export const CustomPortableTextComponents = {
	block: {
		h1: ({ children, value }: PortableTextComponentProps<PortableTextBlock>) => {
			const id = (value.markDefs?.find((s) => s._type === 'addingHtmlId')?.id as string) ?? null;
			return (
				<h1
					id={id}
					className={`my-4 font-extrabold ${id ? 'scroll-mt-36' : ''}`}
					data-ink="medium"
					data-type-style="title-1"
				>
					{superScriptify(children, 'text-3xl')}
				</h1>
			);
		},
		h2: ({ children, value }: PortableTextComponentProps<PortableTextBlock>) => {
			const id = (value.markDefs?.find((s) => s._type === 'addingHtmlId')?.id as string) ?? null;
			return (
				<h2
					id={id}
					className={`my-4 font-extrabold ${id ? 'scroll-mt-36' : ''}`}
					data-ink="medium"
					data-type-style="title-2"
				>
					{superScriptify(children, 'text-lg')}
				</h2>
			);
		},
		h3: ({ children, value }: PortableTextComponentProps<PortableTextBlock>) => {
			const id = (value.markDefs?.find((s) => s._type === 'addingHtmlId')?.id as string) ?? null;
			return (
				<h3
					id={id}
					className={`${styles.h3CustomStyling} my-4 text-2xl font-extrabold lg:text-4xl ${id ? 'scroll-mt-36' : ''}`}
					data-ink="medium"
					data-type-style="title-3"
				>
					{superScriptify(children, 'text-2xl lg:text-4xl')}
				</h3>
			);
		},
		h4: ({ children, value }: PortableTextComponentProps<PortableTextBlock>) => {
			const id = (value.markDefs?.find((s) => s._type === 'addingHtmlId')?.id as string) ?? null;
			return (
				<h4
					id={id}
					className={`my-4 text-2xl font-extrabold normal-case ${id ? 'scroll-mt-36' : ''}`}
					data-ink="medium"
				>
					{superScriptify(children, 'text-xl')}
				</h4>
			);
		},
		h5: ({ children, value }: PortableTextComponentProps<PortableTextBlock>) => {
			const id = (value.markDefs?.find((s) => s._type === 'addingHtmlId')?.id as string) ?? null;
			return (
				<h5 id={id} className="my-4 normal-case" data-ink="medium" data-type-style="subtitle-2">
					{superScriptify(children, 'text-sm')}
				</h5>
			);
		},
		h6: ({ children, value }: PortableTextComponentProps<PortableTextBlock>) => {
			const id = (value.markDefs?.find((s) => s._type === 'addingHtmlId')?.id as string) ?? null;
			return (
				<h6 id={id} className="my-4 normal-case" data-ink="medium" data-type-style="subtitle-3">
					{superScriptify(children, 'text-sm')}
				</h6>
			);
		},
		normal: ({ children }: PortableTextComponentProps<PortableTextBlock>) => {
			return (
				<p className="leading-1 mb-4 font-normal" data-type-style="body-3">
					{superScriptify(children)}
				</p>
			);
		},
		blockquote: ({ children }: PortableTextComponentProps<PortableTextBlock>) => (
			<blockquote className="my-4 border-l-4 border-gray-300 pl-4 text-gray-600">
				{superScriptify(children, 'text-gray-600')}
			</blockquote>
		),
		'eyebrow-1': ({ children }: PortableTextComponentProps<PortableTextBlock>) => (
			<span className="block w-full max-w-full whitespace-normal break-words px-4 text-center font-['brandon-grotesque'] text-base text-[var(--ink-highlight)] lg:text-2xl">
				{children}
			</span>
		),
	},

	types: {
		styledImage: ({ value }: PortableTextTypeComponentProps<PortableTextTypeImageComponent>) => {
			const alignment = stegaCleanValue(value?.alignment) || 'center';
			const caption = stegaCleanValue(value?.caption);
			const altText = stegaCleanValue(value?.altText) || 'Image';
			const imageUrl = stegaCleanValue(value?.imageUrl) || null;
			const alignmentClass = {
				left: 'mx-0 mr-auto',
				center: 'mx-auto',
				right: 'mx-0 ml-auto',
			}[alignment];

			if (!imageUrl) return null;

			return (
				<figure className={`my-6 w-fit ${alignmentClass}`}>
					<Image
						src={addQueryParamToUrl(imageUrl, 'fm=webp')}
						width={768}
						alt={altText}
						className="max-w-full rounded"
					/>
					{caption && (
						<figcaption className="mt-2 text-center text-sm text-gray-500">{caption}</figcaption>
					)}
				</figure>
			);
		},
		table: ({ value }: PortableTextTypeComponentProps<StructuredTableBlock>) => {
			if (!value?.rows || !Array.isArray(value.rows)) return null;

			return (
				<div className="my-4 overflow-x-auto">
					<table className="min-w-full border-collapse border border-gray-300">
						<tbody>
							{value.rows.map((row, rowIndex) => (
								<tr key={rowIndex}>
									{row.cells.map((cell, cellIndex) =>
										rowIndex === 0 ? (
											<th
												key={cellIndex}
												className="border border-gray-300 bg-slate-100 px-4 py-2 text-sm font-normal"
											>
												{cell}
											</th>
										) : (
											<td
												key={cellIndex}
												className="border border-gray-300 px-4 py-2 text-sm font-normal"
											>
												{cell}
											</td>
										)
									)}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			);
		},
	},

	list: {
		bullet: ({ children, value }: PortableTextComponentProps<PortableTextBlock>) => {
			const level = value?.level ?? 1;
			const indentClass = getIndentClass(level);
			return (
				<ul
					className={`list-['-'] font-normal tracking-wide *:py-2 *:pl-2 *:has-[a]:-ml-2 *:has-[a]:list-none *:has-[a]:pl-0 ${indentClass}`}
					data-type-style="body-3"
				>
					{children}
				</ul>
			);
		},
		number: ({ children, value }: PortableTextComponentProps<PortableTextBlock>) => {
			const level = value?.level ?? 1;
			const indentClass = getIndentClass(level);
			return (
				<ol className={`mb-4 list-decimal font-normal ${indentClass}`} data-type-style="body-3">
					{children}
				</ol>
			);
		},
	},

	marks: {
		link: ({ children, value }: PortableTextMarkComponentProps) => {
			const rel = value?.href.startsWith('/') ? 'noreferrer noopener' : undefined;
			return (
				<a
					className="font-normal text-[--ink-medium] underline hover:text-[--ink-highlight]"
					data-type-style="body-3"
					href={value?.href}
					rel={rel}
				>
					{children}
				</a>
			);
		},
		strong: ({ children }: PortableTextMarkComponentProps) => (
			<strong className="font-bold">{children}</strong>
		),
		em: ({ children }: PortableTextMarkComponentProps) => <em className="italic">{children}</em>,
		underline: ({ children }: PortableTextMarkComponentProps) => (
			<span className="underline">{children}</span>
		),
		'strike-through': ({ children }: PortableTextMarkComponentProps) => (
			<span className="line-through">{children}</span>
		),
		code: ({ children }: PortableTextMarkComponentProps) => (
			<code className="rounded bg-[--ink-subtle] px-1 py-0.5 font-mono text-sm">{children}</code>
		),
		superscript: ({ children }: PortableTextMarkComponentProps) => (
			<sup className="text-xs">{children}</sup>
		),
		textColor: ({ children, value }: PortableTextMarkComponentProps<TextColorMark>) => {
			const color = stegaCleanValue(value?.color) ?? 'primary';
			const colorClass =
				{
					primary: 'text-[var(--ink-primary)]',
					secondary: 'text-[var(--ink-base)]',
					accent: 'text-[var(--ink-link)]',
					Highlight: 'text-[var(--ink-highlight)]',
				}[color] || '';
			return <span className={colorClass}>{children}</span>;
		},
		textAlignment: ({ children, value }: PortableTextMarkComponentProps<TextAlignmentMark>) => {
			const alignment = stegaCleanValue(value?.alignment) ?? 'center';
			const alignmentClass = {
				left: 'text-left',
				center: 'text-center',
				right: 'text-right',
				justify: 'text-justify',
			}[alignment];
			return <span className={`${alignmentClass} block`}>{children}</span>;
		},
		addingHtmlId: ({ children }: PortableTextMarkComponentProps) => children,
	},
};
